export type CoverImagePanAxis = 'x' | 'y' | 'none';

const ASPECT_EPSILON = 0.001;
const FOCUS_FACTOR = 1000;
const RUBBER_BAND_RESISTANCE = 0.28;
const RUBBER_BAND_MAX = 0.055;

export function getCoverImagePanAxis(
  containerWidth: number,
  containerHeight: number,
  imageWidth: number,
  imageHeight: number,
): CoverImagePanAxis {
  if (
    containerWidth <= 0 ||
    containerHeight <= 0 ||
    imageWidth <= 0 ||
    imageHeight <= 0
  ) {
    return 'none';
  }

  const containerAspect = containerWidth / containerHeight;
  const imageAspect = imageWidth / imageHeight;

  if (Math.abs(imageAspect - containerAspect) < ASPECT_EPSILON) {
    return 'none';
  }

  return imageAspect > containerAspect ? 'x' : 'y';
}

export function getCoverOverflow(
  containerWidth: number,
  containerHeight: number,
  imageWidth: number,
  imageHeight: number,
): { overflowX: number; overflowY: number } {
  const scale = Math.max(
    containerWidth / imageWidth,
    containerHeight / imageHeight,
  );
  const scaledWidth = imageWidth * scale;
  const scaledHeight = imageHeight * scale;

  return {
    overflowX: Math.max(0, scaledWidth - containerWidth),
    overflowY: Math.max(0, scaledHeight - containerHeight),
  };
}

export function clampFocus(value: number): number {
  const clamped = Math.min(1, Math.max(0, value));
  return Math.trunc(clamped * FOCUS_FACTOR) / FOCUS_FACTOR;
}

/** grab-and-drag: pointer 이동 방향과 동일하게 이미지를 끌어 focus 갱신 (clamp 없음) */
export function applyPanDeltaRaw(
  focusX: number,
  focusY: number,
  deltaX: number,
  deltaY: number,
  axis: CoverImagePanAxis,
  overflowX: number,
  overflowY: number,
): { focusX: number; focusY: number } {
  if (axis === 'x' && overflowX > 0) {
    return {
      focusX: focusX - deltaX / overflowX,
      focusY,
    };
  }

  if (axis === 'y' && overflowY > 0) {
    return {
      focusX,
      focusY: focusY - deltaY / overflowY,
    };
  }

  return { focusX, focusY };
}

export function applyRubberBandFocus(value: number): number {
  if (value >= 0 && value <= 1) {
    return value;
  }

  const excess = value < 0 ? -value : value - 1;
  const band = Math.min(RUBBER_BAND_MAX, excess * RUBBER_BAND_RESISTANCE);
  return value < 0 ? -band : 1 + band;
}

export function toRubberBandVisualFocus(
  raw: { focusX: number; focusY: number },
  axis: CoverImagePanAxis,
): { focusX: number; focusY: number } {
  if (axis === 'x') {
    return {
      focusX: applyRubberBandFocus(raw.focusX),
      focusY: raw.focusY,
    };
  }

  if (axis === 'y') {
    return {
      focusX: raw.focusX,
      focusY: applyRubberBandFocus(raw.focusY),
    };
  }

  return raw;
}

export function clampFocusPair(focus: { focusX: number; focusY: number }) {
  return {
    focusX: clampFocus(focus.focusX),
    focusY: clampFocus(focus.focusY),
  };
}

export function needsRubberBandSnap(
  visual: { focusX: number; focusY: number },
  clamped: { focusX: number; focusY: number },
  axis: CoverImagePanAxis,
): boolean {
  if (axis === 'x') {
    return visual.focusX !== clamped.focusX;
  }
  if (axis === 'y') {
    return visual.focusY !== clamped.focusY;
  }
  return false;
}
